import * as React from 'react';

import {Form, Formik, FormikProps} from 'formik';

import {Input, Select, AutoComplete} from 'antd';
import {LabeledValue} from 'antd/es/select';

import * as Yup from 'yup';

import {Redirect} from 'react-router-dom';

import {List} from '@common/typescript/objects/List';

import {rest} from '@app/components/Api';
import {ListOption} from '@app/objects/SidebarProps';
import {stringBeautifyValidator, numberBeautifyValidator} from '@app/components/LocalCommon/Utils/Validators';
import {eventToLink} from '@app/components/LocalCommon/Utils/eventToLink';

import '@app/scss/components/qualityOfLifeAssessment.scss';
import withSeo from '@app/components/LocalCommon/HigherOrderComponents/WithSeo';

const {Option} = Select;

interface DecoratedSelectProps {
	optionList: ListOption[];
	formikBag: FormikProps<any>;
	fieldName: string;
	fieldValue: string | LabeledValue;
	onChange?: () => void;
	disabled?: boolean;
}

const DecoratedSearch = ({
	formikBag,
	fieldName,
	type = 'text',
	widthResponse = false,
	handleOnChange = (e, formikBag: any, fieldName: string) => {
		formikBag.setFieldValue(fieldName, e.target.value);
	},
	handleOnKeyPress = (e) => {}
}) => {
	return (
		<div className={`decorated-search highlight ${widthResponse ? 'highlight__width-response' : ''}`}>
			<Input
				onChange={e => handleOnChange(e, formikBag, fieldName)}
				onKeyPress={handleOnKeyPress}
				type={type}
				value={formikBag.values.fieldName}
				hidden={widthResponse ? true : false}
			/>
			{widthResponse &&
				<div
					className="decorated-search-mask-input"
					contentEditable={true}
					onKeyUp={(e) => {
						const divText = e.target as HTMLElement;
						formikBag.setFieldValue(fieldName, divText.innerText);
					}}
				/>
			}
		</div>
	);
};

const QolScale = '/how-will-i-know-it-is-time/lap-of-love-quality-of-life-scale.pdf';

const DecoratedSelect = ({
	 optionList,
	 formikBag,
	 fieldName,
	 fieldValue,
	 onChange = (value) => {},
	 disabled = false,
	 labelInValue = false,
	 classCustom = ''
}) => {
	return (
		<div className={`decorated-select highlight ${classCustom}`}>
			<Select
				onChange={(value) => {
					formikBag.setFieldValue(fieldName, value);
					onChange(value);
				}}
				value={fieldValue}
				dropdownClassName="decorated-select-dropdown"
				dropdownMatchSelectWidth={false}
				disabled={disabled}
				labelInValue={labelInValue}
			>
				{
					optionList.map((item, index) => (
						<Option key={item.name + index} value={item.id ? item.id : item.value}>
							{item.name}
						</Option>
					))
				}
			</Select>
		</div>
	);
};

const DecoratedAutoComplete = ({
	optionList,
	formikBag,
	fieldName,
	fieldValue,
	disabled = false,
}) => {
	const [name, setName] = React.useState('');

	React.useEffect(() => {
		setName(fieldValue);
	}, [fieldValue]);

	const options = optionList.map((item) => {
		return {text: item.name, value: item.id };
	});

	return (
		<div className={'decorated-autoComplete highlight'}>
			<AutoComplete
				dropdownClassName="decorated-select-dropdown"
				dropdownMatchSelectWidth={false}
				onSearch={(value) => {
					setName(value);
				}}
				onSelect={(value, option) => {
					setName(option.children);
					formikBag.setFieldValue(fieldName, value);
				}}
				dataSource={options}
				onBlur={() => {
					let isSelected = true;
					for (let i = 0; i < options.length; i++) {
						isSelected = options[i].text === name;

						if (isSelected) {
							break;
						}
					}

					!isSelected && setName('');
				}}

				value={name}
				disabled={disabled}
				filterOption={(inputValue, option) => option?.props?.children?.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1}
			>
				<input type="text" size={name?.length || 3} />
			</AutoComplete>
		</div>
	);
};

interface Disease {
	name: string;
	deleted: boolean;
	id: number;
}

export enum DotLocation {
	Head = 1,
	HeadFront = 2,
	Body = 3,
	Legs = 4,
	BackLegs = 5,
	Belly = 6,
	Back = 7
}

export const tooltipDotLocation = {
	1: 'Mental Health',
	2: 'Natural Functions',
	3: 'Physical Health',
	4: 'Physical Health',
	5: 'Natural Functions',
	6: 'Natural Functions',
	7: 'Natural Functions',
};

export interface Question {
	text: string;
	category: string;
	dotLocation: DotLocation;
}

interface PetInfo {
	years: string;
	gender?: number | null;
	weight?: number | null;
	petType: string;
	name: string;
	hasDiagnosis: boolean | null;
	disease: number | null;
}

export interface Session {
	questions: Question[];
	sessionId: string;
	petType: 'dog' | 'cat' | 'other';
	petName: string;
}

const validationSchema = Yup.object({
	years: numberBeautifyValidator('Years is a required field'),
	petType: stringBeautifyValidator('Pet type is a required field'),
	name: stringBeautifyValidator('Name is a required field'),
	hasDiagnosis: Yup.boolean().required('Has diagnosis field must be completed.'),
	gender: Yup.number().min(1).max(2).required('Gender field must be completed'),
	weight: numberBeautifyValidator('Weight is a required field'),
	disease: Yup.number().nullable(true).when('hasDiagnosis', {
		is: true,
		then: Yup.number().integer().min(-1).nullable(false).required('Disease field must be completed'),
	})
});

const QualityOfLifeAssessment = () => {
	const [diseaseList, setDiseaseList] = React.useState<Disease[]>([]);
	const [questions, setQuestions] = React.useState<Question[]>([]);
	const [currentQuestion, setCurrentQuestion] = React.useState(0);
	const [petType, setPetType] = React.useState<string>('');
	const [pet, setPet] = React.useState<PetInfo>({
		years: '',
		petType: '',
		name: '',
		weight: null,
		gender: null,
		hasDiagnosis: null,
		disease: null});
	const [sessionId, setSessionId] = React.useState('');
	React.useEffect(() => {
		rest.get<List<Disease>>(`v1/disease/${petType}`)
			.then((res) => {
				setDiseaseList([...res.list, {name: 'Disease not listed', id: -1, deleted: false}]);
			});
	}, [petType]);

	const handleOnChange = (e, formikBag, fieldName) => {
		const input = e.target as HTMLInputElement;
		formikBag.setFieldValue(fieldName, parseInt(input.value, 10));
		input.value = parseInt(input.value, 10).toString();
	};

	const handleOnKeyPress = (e) => {
		if (!(e.charCode >= 48 && e.charCode <= 57)) {
			e.preventDefault();
			return false;
		}
	};

	return (
		<div className="pet-data-form__wrapper">
			<div className="pet-data-form">
				<h1>Quality-of-Life Assessment</h1>
				<h6>
					Curious or concerned about your pet's quality of life? It only takes a few questions for us to
					provide you with more information.
				</h6>
				<Formik
					onSubmit={(values, formikActions) => {
						!values.disease && formikActions.setFieldValue('disease', 0);
						!values.disease ? setPet({...values, disease: 0}) : setPet(values);
						values.petType !== 'other' ?
						rest.post<Session>('v1/qol', !values.disease ? {...values, disease: 0} : values)
							.then((res) => {
								setSessionId(res.sessionId);
								setQuestions(res.questions);
								setCurrentQuestion(0);
							})
							:
						setQuestions([{
							text: '',
							dotLocation: DotLocation.Head,
							category: ''
						}]);
					}}

					initialValues={{years: '', petType: '', name: '', hasDiagnosis: null, disease: null}}
					validationSchema={validationSchema}
					render={(formikBag: FormikProps<any>) => {

						return (
							!questions.length ?
								<Form>
							<span className="pet-data-form__row">
								I have a
								<DecoratedSearch
									type="number"
									formikBag={formikBag}
									fieldName={'years'}
									handleOnChange={handleOnChange}
									handleOnKeyPress={handleOnKeyPress}
								/>
								year <br className={'xs-break'}/>old,&nbsp;
								<DecoratedSelect
									optionList={[{name: 'male', value: 1}, {name: 'female', value: 2}]}
									formikBag={formikBag}
									fieldName={'gender'}
									fieldValue={formikBag.values['gender']}
									onChange = {(value) => {
										formikBag.setFieldTouched('gender', true);
									}}
								/>
								&nbsp;
								<br className={'sm-break'}/>
								<DecoratedSelect
									optionList={[{name: 'dog', value: 'dog'}, {name: 'cat', value: 'cat'}, {name: 'other', value: 'other'}]}
									formikBag={formikBag}
									fieldName={'petType'}
									fieldValue={formikBag.values['petType']}
									onChange = {(value) => {
										formikBag.setFieldTouched('petType', true);
										setPetType(value);
									}}
								/>
								<br className={'xs-break'}/>
								named <DecoratedSearch formikBag={formikBag} fieldName={'name'} widthResponse={true}/>,
							</span>
									<br/>
									<span className="pet-data-form__row">
								who weighs
								<DecoratedSearch
									type="number"
									formikBag={formikBag}
									fieldName={'weight'}
									handleOnChange={handleOnChange}
									handleOnKeyPress={handleOnKeyPress}
								/>
								lbs and
								<DecoratedSelect
									optionList={[{name: 'has', value: true}, {name: 'has not', value: false}]}
									formikBag={formikBag}
									fieldName={'hasDiagnosis'}
									fieldValue={formikBag.values['hasDiagnosis']}
									onChange = {() => {
										formikBag.setFieldTouched('hasDiagnosis', true);
										formikBag.values['hasDiagnosis'] && formikBag.setFieldValue('disease', 0);
									}}
									disabled={false}
								/>
								been <br className={'sm-break'}/> diagnosed with <br className={'sm-break'}/><br className={'xs-break'}/>
								<DecoratedAutoComplete
									optionList={diseaseList}
									formikBag={formikBag}
									fieldName={'disease'}
									fieldValue={!formikBag.values['hasDiagnosis'] && formikBag.touched['hasDiagnosis'] ?
										'a disease' : ''}
									disabled={!formikBag.values['hasDiagnosis']}
								/>.
							</span>
									<br/>
									{
										Object.values(formikBag.errors).some(el => el) &&
										formikBag.submitCount > 0 &&
										<div className="submit-validate-error" style={{color: 'red', marginBottom: '16px'}}>All fields should be completed</div>
									}

									<button type="submit" className="btn btn-primary btn-md">Continue</button>
								</Form> :
								formikBag.values['petType'] !== 'other' ?
								<Redirect to={{
									pathname: `/quality-of-life-assessment/question/${0}`,
									state: {questions: questions, sessionId: sessionId, petType: pet.petType, petName: pet.name}
								}}
								/> :
									<Redirect to={{
										pathname: '/quality-of-life-assessment/different-species',
										state: {name: formikBag.values['name']}
									}}
									/>
						);
					}}
				/>

				<div className="pet-data-form__footer">
					<a href={QolScale} target="_blank" onClick={() => eventToLink('Download a printable copy of our Lap of Love Quality of Life Scale')}>
						Download a printable copy of our <b>Lap of Love Quality-of-Life Scale</b>
					</a>
				</div>
			</div>
		</div>

	);
};

export default withSeo(QualityOfLifeAssessment, state => state.qualityOfLifePage, 'qualityOfLifePage', 'quality-of-life-assessment');